import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
} from '@chakra-ui/react';
import { useController, useFormContext } from 'react-hook-form';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import type { OrganisationIdentityFormValues } from '@duke-hq/svc-api/admin/trpc/routers/organisation/schemas';

/**
 * A hex colour. Must be hex because emails carry it as an inline style, so this
 * does not use the Chakra token picker used elsewhere.
 */
export type ColourFieldName = 'linkColour' | 'buttonBackgroundColour' | 'buttonTextColour';

export function ColourField({
  name,
  label,
  helper,
}: {
  name: ColourFieldName;
  label: string;
  helper?: string;
}): React.ReactElement {
  const { control } = useFormContext<OrganisationIdentityFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController({ control, name });

  // The native swatch only understands #rrggbb, so a half-typed value falls back
  // to black there while the text input keeps showing exactly what was typed.
  const current = typeof field.value === 'string' ? field.value : '';
  const swatchValue = /^#[0-9A-Fa-f]{6}$/.test(current) ? current : '#000000';

  return (
    <FormControl isInvalid={error != null}>
      {/* Both inputs name themselves: FormControl gives every input it wraps the
          same generated id, so two of them would collide and leave the label
          pointing at whichever won. */}
      <FormLabel fontSize="sm" mb={1} htmlFor={`${name}-hex`}>
        {label}
      </FormLabel>
      <HStack gap={2}>
        <Input
          id={`${name}-swatch`}
          type="color"
          aria-label={label}
          value={swatchValue}
          onChange={(event) => field.onChange(event.target.value.toUpperCase())}
          width={pxToRem(44)}
          minWidth={pxToRem(44)}
          padding={1}
          size="sm"
        />
        <Input
          id={`${name}-hex`}
          value={current}
          onChange={(event) => field.onChange(event.target.value)}
          onBlur={field.onBlur}
          placeholder="#5F51E8"
          size="sm"
          fontFamily="mono"
        />
      </HStack>
      {helper != null && error == null && <FormHelperText fontSize="xs">{helper}</FormHelperText>}
      <FormErrorMessage fontSize="xs">{error?.message}</FormErrorMessage>
    </FormControl>
  );
}
